/*!

=========================================================
* Vision UI Free React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-free-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)
* Licensed under MIT (https://github.com/creativetimofficial/vision-ui-free-react/blob/master LICENSE.md)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Visionware.

*/

/*Bearer ${token}`
  This file is used for controlling the global states of the components,
  you can customize the states for the different components here.
*/

import { createContext, useContext, useReducer } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// The Vision UI Dashboard  Material main context
const VisionUI = createContext();

// Setting custom name for the context which is visible on react dev tools
VisionUI.displayName = "VisionUIContext";

// Vision UI Dashboard React reducer
function reducer(state, action) {
  switch (action.type) {
    case "MINI_SIDENAV": {
      return { ...state, miniSidenav: action.value };
    }
    case "TRANSPARENT_SIDENAV": {
      return { ...state, transparentSidenav: action.value };
    }
    case "SIDENAV_COLOR": {
      return { ...state, sidenavColor: action.value };
    }
    case "TRANSPARENT_NAVBAR": {
      return { ...state, transparentNavbar: action.value };
    }
    case "FIXED_NAVBAR": {
      return { ...state, fixedNavbar: action.value };
    }
    case "OPEN_CONFIGURATOR": {
      return { ...state, openConfigurator: action.value };
    }
    case "DIRECTION": {
      return { ...state, direction: action.value };
    }
    case "LAYOUT": {
      return { ...state, layout: action.value };
    }
    case "CURRENCY": {
      return { ...state, currency: action.value };
    }
    case "FISCAL_YEAR_START": {
      return { ...state, fiscalYearStart: action.value };
    }
    case "DATE_FORMAT": {
      return { ...state, dateFormat: action.value };
    }
    case "DEFAULT_LANDING_PAGE": {
      return { ...state, defaultLandingPage: action.value };
    }
    case "NOTIFICATION_BADGE": {
      return { ...state, notificationBadge: action.value };
    }
    case "AUTO_REFRESH_INTERVAL": {
      return { ...state, autoRefreshInterval: action.value };
    }
    case "DEFAULT_VAT_RATE": {
      return { ...state, defaultVatRate: action.value };
    }
    case "SERVICE_DEFAULTS": {
      return { ...state, serviceDefaults: { ...state.serviceDefaults, ...action.value } };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

// Vision UI Dashboard React context provider
function VisionUIControllerProvider({ children }) {
  const initialState = {
    miniSidenav: false,
    transparentSidenav: false,
    sidenavColor: "success",
    transparentNavbar: true,
    fixedNavbar: true,
    openConfigurator: false,
    direction: "ltr",
    layout: "dashboard",
    currency: "GBP",
    fiscalYearStart: 4,
    dateFormat: "DD/MM/YYYY",
    defaultLandingPage: localStorage.getItem("ledger_defaultLandingPage") || "/client-profiles",
    notificationBadge: true,
    autoRefreshInterval: 0,
    defaultVatRate: 20,
    serviceDefaults: {},  // { [serviceId]: { uid, name } }
  };

  const [controller, dispatch] = useReducer(reducer, initialState);

  return <VisionUI.Provider value={[controller, dispatch]}>{children}</VisionUI.Provider>;
}

// Vision UI Dashboard React custom hook for using context
function useVisionUIController() {
  const context = useContext(VisionUI);

  if (!context) {
    throw new Error("useVisionUIController should be used inside the VisionUIControllerProvider.");
  }

  return context;
}

// Typechecking props for the VisionUIControllerProvider
VisionUIControllerProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// Context module functions
const setMiniSidenav = (dispatch, value) => dispatch({ type: "MINI_SIDENAV", value });
const setTransparentSidenav = (dispatch, value) => dispatch({ type: "TRANSPARENT_SIDENAV", value });
const setSidenavColor = (dispatch, value) => dispatch({ type: "SIDENAV_COLOR", value });
const setTransparentNavbar = (dispatch, value) => dispatch({ type: "TRANSPARENT_NAVBAR", value });
const setFixedNavbar = (dispatch, value) => dispatch({ type: "FIXED_NAVBAR", value });
const setOpenConfigurator = (dispatch, value) => dispatch({ type: "OPEN_CONFIGURATOR", value });
const setDirection = (dispatch, value) => dispatch({ type: "DIRECTION", value });
const setLayout = (dispatch, value) => dispatch({ type: "LAYOUT", value });
const setCurrency = (dispatch, value) => dispatch({ type: "CURRENCY", value });
const setFiscalYearStart = (dispatch, value) => dispatch({ type: "FISCAL_YEAR_START", value });
const setDateFormat = (dispatch, value) => dispatch({ type: "DATE_FORMAT", value });
const setDefaultLandingPage = (dispatch, value) => {
  localStorage.setItem("ledger_defaultLandingPage", value);
  dispatch({ type: "DEFAULT_LANDING_PAGE", value });
};
const setNotificationBadge = (dispatch, value) => dispatch({ type: "NOTIFICATION_BADGE", value });
const setAutoRefreshInterval = (dispatch, value) => dispatch({ type: "AUTO_REFRESH_INTERVAL", value });
const setDefaultVatRate = (dispatch, value) => dispatch({ type: "DEFAULT_VAT_RATE", value });
const setServiceDefault = (dispatch, serviceId, uid, name) =>
  dispatch({ type: "SERVICE_DEFAULTS", value: { [serviceId]: uid ? { uid, name } : null } });

export {
  VisionUIControllerProvider,
  useVisionUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setSidenavColor,
  setTransparentNavbar,
  setFixedNavbar,
  setOpenConfigurator,
  setDirection,
  setLayout,
  setCurrency,
  setFiscalYearStart,
  setDateFormat,
  setDefaultLandingPage,
  setNotificationBadge,
  setAutoRefreshInterval,
  setDefaultVatRate,
  setServiceDefault,
};


